import { Box, Button, Divider, Drawer, List, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { useMemo } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Tag_Rule_Entity_Type_Enum } from '@kheops/graphql';
import FullTextSearch from '../../common/components/FullTextSearch';
import useOpenable from '../../hooks/useOpenable';
import GeoLocationFilter from '../../search/filters/GeoLocationFilter';
import ProductLabelsFilter from '../filters/ProductLabelsFilter';
import DiscoveryViewSwitch from '../DiscoveryViewSwitch';
import { discoveryViewAtom } from '../state';
import SocialTagFilter from './filters/SocialTagFilter';
import CompanyCategoryFilter from './filters/CompanyCategoryFilter';
import CompanyDeliveryMovFilter from './filters/CompanyDeliveryMovFilter';

export interface DiscoveryFiltersProps {
  fullTextSearchPlaceholder: string;
  hideLabelsFilter?: boolean;
  index: string;
}

export default function DiscoveryFilters({ fullTextSearchPlaceholder, hideLabelsFilter, index }: DiscoveryFiltersProps): React.JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation(['common', 'discovery', 'search']);
  const { indexUiState } = useInstantSearch();
  const { isOpen, open, close } = useOpenable();
  const discoveryView = useAtomValue(discoveryViewAtom);

  const tagEntity = useMemo((): Tag_Rule_Entity_Type_Enum => {
    return discoveryView === 'SUPPLIERS' ? Tag_Rule_Entity_Type_Enum.Supplier : Tag_Rule_Entity_Type_Enum.BaseUnit;
  }, [discoveryView]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'start' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: { xs: 1, lg: 2 },
          }}
        >
          <FullTextSearch
            inputProps={{
              placeholder: fullTextSearchPlaceholder,
              fullWidth: true,
              sx: {
                width: {
                  lg: '623px',
                },
              },
            }}
            value={indexUiState.query}
          />
          <Box
            sx={{
              display: {
                xs: 'none',
                lg: 'flex',
              },
            }}
          >
            <DiscoveryViewSwitch />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%', overflow: 'auto' }}>
          <Button
            variant="text"
            startIcon={<TuneIcon />}
            onClick={open}
          >
            {t('search:all_filters')}
          </Button>
          {discoveryView === 'SUPPLIERS' && (
            <>
              <CompanyDeliveryMovFilter mode="POPOVER" />
              <CompanyCategoryFilter mode="POPOVER" />
            </>
          )}
          <SocialTagFilter
            entity={tagEntity}
            level={1}
            label={t('discovery:attractivity')}
            icon={<TrendingUpOutlinedIcon />}
            mode="POPOVER"
          />
          <SocialTagFilter
            entity={tagEntity}
            level={2}
            label={t('discovery:advantages')}
            icon={<WorkspacePremiumOutlinedIcon />}
            mode="POPOVER"
          />
        </Box>
      </Box>
      <Drawer
        keepMounted
        open={isOpen}
        onClose={close}
        anchor={isMobile ? 'bottom' : 'right'}
        slotProps={{
          paper: {
            sx: {
              gap: 2,
              width: {
                xs: undefined,
                lg: 375,
                borderRadius: '24px 0 0 24px',
              },
            },
          },
        }}
      >
        <Box sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, position: 'sticky', top: 1, zIndex: 10, backgroundColor: 'white' }}>
            <Typography variant="headlineSmall" sx={{ lineHeight: '40px' }}>
              {t('common:filters')}
            </Typography>
            <Button variant="text" onClick={close}>
              <CloseIcon />
            </Button>
          </Box>
          <List
            sx={{
              pt: 1.5,
            }}
          >
            <GeoLocationFilter mode="ACCORDION" index={index} />
            <Divider />
            {!hideLabelsFilter && (
              <>
                <ProductLabelsFilter mode="ACCORDION" />
                <Divider />
              </>
            )}
            {discoveryView === 'SUPPLIERS' && (
              <>
                <CompanyDeliveryMovFilter mode="ACCORDION" />
                <Divider />
                <CompanyCategoryFilter mode="ACCORDION" />
                <Divider />
              </>
            )}
            <SocialTagFilter
              entity={tagEntity}
              level={1}
              label={t('discovery:attractivity')}
              icon={<TrendingUpOutlinedIcon />}
              mode="ACCORDION"
            />
            <Divider />
            <SocialTagFilter
              entity={tagEntity}
              level={2}
              label={t('discovery:advantages')}
              icon={<WorkspacePremiumOutlinedIcon />}
              mode="ACCORDION"
            />
          </List>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            position: 'sticky',
            zindex: 10,
            bottom: 0,
            mt: 'auto',
          }}
        >
          <Divider flexItem sx={{ width: '100%' }} />
          <Button variant="contained" onClick={close} sx={{ mx: 'auto', my: 2 }}>
            {t('search:validate_changes')}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
